import React from "react";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
  Paper,
  Collapse,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AddToHomeScreenIcon from "@mui/icons-material/AddToHomeScreen";
import Cached from "@mui/icons-material/Cached";
import Logout from "@mui/icons-material/Logout";
import AjustesDialog, { SECCIONES as SECCIONES_AJUSTES } from "../ajustes/AjustesDialog";
import BarraAccesoV4 from "./BarraAccesoV4";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SettingsIcon from "@mui/icons-material/Settings";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SmardxTitle from "../typography/SmardxTitle";
import { useCurrentUser } from "../context/UserContext";
import { HERRAMIENTAS } from "./herramientas";
import { useThemeMode } from "../context/ThemeModeContext";
import { logout } from "../../helpers/authentication-helpers";
import WeeklyUsagePill from "../WeeklyUsagePill";
import { isStandalone, openInstallGuide } from "../../helpers/pwa-install";
import { useNavigate, useLocation } from "react-router";
import Button from "@mui/material/Button";

// Rutas que montan <Home /> (App.js) y por lo tanto la barra propia de home_v4.html.
export const RUTAS_HOME = ["/", "/home"];
// React Router resuelve "/home/" y "/Home" a la misma ruta (barra final y mayúsculas no
// cuentan); la comparación tiene que hacer lo mismo o en esas variantes vuelven las dos
// barras encimadas (revisor #1439, ronda 2).
export const esRutaHome = (pathname = "") =>
  RUTAS_HOME.includes((pathname.replace(/\/+$/, "") || "/").toLowerCase());

const DxNavbar = () => {
  const { currentUser } = useCurrentUser();
  const { mode: themeMode, toggle: toggleTheme, setMode: setThemeMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [visible, setVisible] = React.useState(true);
  const isSuperuser = Boolean(currentUser?.is_superuser);
  // En /turno mobile colapsamos el navbar global a su mínima expresión:
  // ocultamos el back arrow flotante (redundante con browser back) y los
  // 4 botones circulares de la derecha (settings/refresh/avatar/logout) —
  // esas acciones quedan disponibles desde el menú hamburger. La idea
  // es liberar los ~84px verticales que el navbar global gastaba arriba
  // del cockpit V2.
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Ancho de sobra en el header. El pill del logo va CENTRADO y el cluster
  // anclado a la derecha, así que todo lo que crece el cluster avanza sobre el
  // centro — donde vive el botón ≡. Bajo este umbral se sacan las dos piezas
  // que más lo ensanchan:
  //   · la píldora de uso semanal (#1209), que pasa al menú (no desaparece);
  //   · el NOMBRE del usuario al lado del avatar, que queda solo con el avatar.
  // El nombre no es cosmético: con "Dra. Constanza Bozan Sepulveda" a 769 px el
  // cluster tapaba el ≡ y el menú no abría — bug PRE-EXISTENTE, medido con y
  // sin la píldora el 08-sep-2026.
  const hayEspacioAncho = useMediaQuery("(min-width: 1200px)");
  // Y un umbral más alto para la píldora COMPLETA: con un nombre largo, bajo
  // 1440 px la versión con texto y barra alcanza al ≡, y JUSTO en 1440 la
  // holgura medida era 0 px (con un % de 4 dígitos llegaba a solaparse 7 px).
  // 1500 deja margen real y, de paso, hace que se lea como una píldora suelta
  // en vez de pegada al pill del logo. Bajo eso va la compacta (punto + %).
  const cabePildoraCompleta = useMediaQuery("(min-width: 1500px)");
  const isMobileTurno =
    isMobile && (location.pathname || "").startsWith("/turno");
  // #1453 R9: sin sesión en el celular, "Crear cuenta" va en la barra cerrada
  // (antes vivía solo dentro del menú ≡). La píldora se ajusta al contenido y
  // se ancla a la derecha para no chocar con la flecha de volver.
  // #1439 B: en las pantallas de acceso (siempre oscuras, AuthShell) no va esta barra sino la
  // de la home v4 en cápsula oscura (BarraAccesoV4, ver el return de abajo).
  const enAcceso = /^\/(authentication|recuperar-clave|nueva-clave)/.test(location.pathname || "");
  const registroEnBarra = isMobile && !currentUser && !open;
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // Ajustes (#1521) abre en la sección que pide quien lo abre: engranaje →
  // General, avatar → Perfil. En el celular `null` = la lista de submenús.
  const [ajustesSeccion, setAjustesSeccion] = React.useState("general");
  // Cada apertura monta el popup de cero (key) para que arranque en la sección
  // pedida y no en la que quedó la vez anterior.
  const [ajustesApertura, setAjustesApertura] = React.useState(0);
  const abrirAjustes = (seccion) => {
    setAjustesSeccion(seccion);
    setAjustesApertura((n) => n + 1);
    setSettingsOpen(true);
  };
  const profile = currentUser?.professional_profile;
  const displayName =
    profile?.full_name || currentUser?.username || currentUser?.email || "";
  const initials = (displayName || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const reloadPage = () => {
    if (window.location.pathname === "/waiting-for-access") {
      window.location.href = "/";
    } else {
      window.location.reload();
    }
  };

  // Secciones del landing (anchors) — se muestran como chips "Explorar".
  const sectionItems = [
    { label: "Home", path: "#hero", icon: <HomeOutlinedIcon /> },
    { label: "Servicios", path: "#servicios", icon: <LayersOutlinedIcon /> },
    { label: "Cómo funciona", path: "#plataforma", icon: <MicNoneOutlinedIcon /> },
    { label: "Ética", path: "#etica", icon: <BalanceOutlinedIcon /> },
    { label: "Equipo", path: "#equipo", icon: <GroupOutlinedIcon /> },
    { label: "Conversemos", path: "#contacto", icon: <ChatBubbleOutlineOutlinedIcon /> },
  ];

  // Herramientas (modos) — grid de cards. `short` = etiqueta compacta para la card.
  // La lista vive en ./herramientas (la comparte la home v4); acá solo se le pone el ícono.
  const ICONO_HERRAMIENTA = {
    "/raw-query": <MonitorHeartOutlinedIcon />,
    "/complicaciones": <WarningAmberOutlinedIcon />,
    "/transcriptor": <MicNoneOutlinedIcon />,
    "/turno": <ViewColumnOutlinedIcon />,
    "/informes": <DescriptionOutlinedIcon />,
    "/epicrisis-radonc": <BiotechOutlinedIcon />,
    "/finder": <SearchOutlinedIcon />,
    "/visualizador-examenes": <VisibilityOutlinedIcon />,
    "/recetas-ambulatorias": <MedicationOutlinedIcon />,
    "/recetas-ambulatorias/historial": <HistoryOutlinedIcon />,
  };
  const allFunctionalItems = HERRAMIENTAS.map((h) => ({ ...h, icon: ICONO_HERRAMIENTA[h.path] }));

  const functionalItems = allFunctionalItems.filter(
    (item) => isSuperuser || Boolean(currentUser?.[item.flag])
  );

  // Cuenta / admin — chips del footer (separado de las herramientas).
  const accountItems = [];
  if (currentUser) {
    accountItems.push({ label: "Comunidad", path: "/comunidad", icon: <GroupsOutlinedIcon /> });
  }
  if (isSuperuser) {
    accountItems.push({ label: "Usuarios", path: "/admin/users", icon: <AdminPanelSettingsOutlinedIcon /> });
    accountItems.push({ label: "Cambios por revisar", path: "/admin/pendientes", icon: <PendingActionsOutlinedIcon /> });
    accountItems.push({ label: "Cambios de fichas", path: "/admin/changelog", icon: <FactCheckOutlinedIcon /> });
  }

  // ===== Estilos del menú "command center" (variante A) =====
  const secLabelSx = {
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
    textTransform: "uppercase", color: "text.faint", mb: 1, ml: 0.5,
  };
  const chipBaseSx = {
    display: "inline-flex", alignItems: "center", gap: 0.75,
    px: 1.5, py: 0.85, borderRadius: 999, fontSize: 13, fontWeight: 600,
    cursor: "pointer", color: "text.primary",
    border: "1px solid rgba(var(--fg-rgb),0.1)",
    backgroundColor: "rgba(var(--fg-rgb),0.04)",
    transition: "all 140ms ease",
    "& svg": { color: "text.secondary" },
    "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.09)", borderColor: "rgba(var(--fg-rgb),0.22)" },
  };
  const chipHomeSx = {
    ...chipBaseSx,
    backgroundColor: "rgba(31,111,151,0.16)", borderColor: "rgba(31,111,151,0.4)",
    "& svg": { color: "var(--color-primary)" },
  };
  // #1439 (mockup tools/mockups/smartdx_mockups_navbar_acceso_e_v2.html): sin
  // sesión, "Acceder" + menú pasa a dos botones a la vista. "Crear cuenta
  // gratis" es la píldora contrastada de la landing E (texto sobre fondo
  // invertido, sigue el modo) e "Ingresar" queda fantasma al lado.
  const crearCuentaSx = {
    height: 34, px: 1.75, borderRadius: "999px", textTransform: "none",
    fontWeight: 600, whiteSpace: "nowrap",
    color: "var(--bg-base)", backgroundColor: "var(--text-primary)",
    "&:hover": { backgroundColor: "var(--text-secondary)" },
  };
  const ingresarSx = {
    height: 34, px: 1.5, borderRadius: "999px", textTransform: "none",
    fontWeight: 600, whiteSpace: "nowrap",
    color: "text.primary", backgroundColor: "transparent",
    "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.08)" },
  };
  const cardSx = {
    display: "flex", alignItems: "center", gap: 1.25, p: 1.25,
    borderRadius: "13px", cursor: "pointer",
    border: "1px solid rgba(var(--fg-rgb),0.07)",
    backgroundColor: "rgba(var(--fg-rgb),0.03)",
    transition: "all 150ms ease",
    "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.07)", borderColor: "rgba(31,111,151,0.45)", transform: "translateY(-1px)" },
  };
  const cardIcoSx = {
    flex: "0 0 34px", width: 34, height: 34, borderRadius: "9px",
    display: "grid", placeItems: "center",
    backgroundColor: "rgba(31,111,151,0.14)", color: "var(--color-primary)",
  };

  const scrollToHash = (hash) => {
    const targetId = hash.replace("#", "");
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNav = (path) => {
    setOpen(false);
    if ((path === "/" || path === "#") && location.pathname === "/") {
      navigate("/", { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (path.startsWith("#")) {
      const hash = path;
      const sameHash = location.pathname === "/" && location.hash === hash;
      navigate({ pathname: "/", hash: hash });
      // Forzar scroll suave incluso si ya estamos en la misma sección
      setTimeout(() => scrollToHash(hash), sameHash ? 20 : 80);
    } else {
      navigate(path);
    }
  };

  const handleToggleMenu = () => setOpen((prev) => !prev);

  // El navbar se desvanece al bajar (`scrollY >= 20`) y vuelve al subir. La
  // suscripción se rehace en cada cambio de ruta Y recalcula de inmediato, sin
  // esperar un evento de scroll: si la ruta nueva no llega a scrollear (el Home
  // es un iframe fijo, el Turno bloquea el scroll del documento), no hay evento
  // que dispare y el navbar se quedaba invisible de la pantalla anterior. Es la
  // mitad defensiva del arreglo del 09-sep-2026; la otra —devolver el scroll
  // arriba al navegar— vive en App.js, con el detalle del caso medido.
  React.useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY < 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  // #1439 (D1): en la home la DxNavbar no dibuja la barra, pero el menú de cuenta de la v4
  // (las iniciales, en el iframe) pide desde ahí abrir Ajustes y cerrar sesión. Solo se
  // aceptan mensajes del mismo origen, estando en la home y que vengan de uno de SUS iframes
  // (no de la propia ventana ni de una ventana abierta aparte).
  React.useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || e.source === window) return;
      if (!esRutaHome(window.location.pathname)) return;
      const deUnIframe = Array.from(document.querySelectorAll("iframe")).some((f) => f.contentWindow === e.source);
      if (!deUnIframe) return;
      const tipo = e.data && e.data.type;
      if (tipo === "smartdx-ajustes") {
        // Igual que en la barra de la app (#1521): "Mi perfil" pide la sección Perfil (como el
        // avatar); "Ajustes" abre en General en escritorio (engranaje) y en la lista de submenús en
        // el celular (chip "Ajustes"), con el mismo corte que AjustesDialog. Solo secciones que existen.
        const pedida = e.data.seccion;
        const valida = SECCIONES_AJUSTES.some((x) => x.id === pedida);
        setAjustesSeccion(valida ? pedida : window.matchMedia("(max-width: 768px)").matches ? null : "general");
        setAjustesApertura((n) => n + 1);
        setSettingsOpen(true);
      } else if (tipo === "smartdx-logout") {
        logout();
      } else if (tipo === "smartdx-theme-set") {
        // #1439: el sol/luna de la barra de la home v4. setMode (no toggle) para que el listener,
        // registrado una sola vez, no alterne con un modo viejo.
        if (e.data.mode === "light" || e.data.mode === "dark") setThemeMode(e.data.mode);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const dialogoAjustes = (
    <AjustesDialog
      key={ajustesApertura}
      open={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      seccionInicial={ajustesSeccion}
    />
  );

  // En la home manda la barra de la landing v4 (#1439): Home.js le pasa la sesión y las
  // herramientas por postMessage, y su panel hace lo que acá hace el menú. Vale para las
  // DOS rutas que montan <Home /> ("/" y "/home", App.js): con solo "/", en /home se
  // dibujaban las dos barras encimadas, y /home es adonde llega tras el login quien
  // tiene más de un módulo (destino-sesion.js). Revisor #1439, 03-oct.
  // Lo único que sí va en la home es el popup de Ajustes (lo abre el menú de cuenta, D1).
  if (esRutaHome(location.pathname)) return dialogoAjustes;
  // #1439 B: en el acceso, la barra de la home v4 (sin Ingresar / Crear cuenta: las pestañas
  // del formulario hacen eso).
  if (enAcceso) return <BarraAccesoV4 />;
  // Cubre R33 — .claude/specs/1599-onboarding-calificar/. La pantalla de lo que falta
  // (CompletarDatos) trae su propia barra (logo + Cerrar sesión, caso F del mockup B): con
  // la barra global encima se veían dos y el "‹" tapaba el logo (lado a lado del 11-oct). Sin
  // navbar tampoco hay salida lateral: R25 deja solo Guardar y Cerrar sesión.
  if ((location.pathname || "").startsWith("/perfil/completar")) return dialogoAjustes;

  return (
    <>
      {/* Botón volver global — oculto en /turno mobile (redundante con browser back)
          y en el onboarding (#1525, revisor F-2): el wizard trae su propio "‹" y con
          los dos flotando a 390 px quedaban casi encimados. */}
      {!isMobileTurno && !(location.pathname || "").startsWith("/perfil/verificacion") && (
        <Box
          sx={{
            position: "fixed",
            // El viewport arranca DEBAJO de la Dynamic Island solo si lo pedimos:
            // index.html declara viewport-fit=cover, así que sin el inset este
            // 12px queda tapado por la cámara (se ve en la PWA instalada, donde
            // no hay barra de Safari amortiguando).
            top: "max(12px, env(safe-area-inset-top))",
            left: "max(12px, env(safe-area-inset-left))",
            // Por debajo del Dialog (1300) para que el viewer full-screen
            // lo tape; cuando el menú está abierto, el overlay del menú
            // (1400) ya cubre todo igual.
            zIndex: 1100,
            display: "flex",
          }}
        >
          <IconButton
            data-testid="navbar-volver"
            onClick={() => window.history.back()}
            sx={{
              backgroundColor: themeMode === "dark"
                ? "rgba(var(--shadow-rgb),0.6)"
                : "rgba(255,255,255,0.88)",
              border: themeMode === "dark"
                ? "1px solid rgba(var(--fg-rgb),0.15)"
                : "1px solid rgba(15,23,42,0.14)",
              boxShadow: themeMode === "dark"
                ? "0 4px 12px rgba(0,0,0,0.35)"
                : "0 2px 10px rgba(15,23,42,0.10)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              "&:hover": {
                backgroundColor: themeMode === "dark"
                  ? "rgba(var(--shadow-rgb),0.7)"
                  : "rgba(255,255,255,1)",
                borderColor: themeMode === "dark"
                  ? "rgba(var(--fg-rgb),0.22)"
                  : "rgba(15,23,42,0.22)",
              },
            }}
          >
            <ArrowBackIosNewIcon sx={{ color: "text.primary", fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      <Box
        onClick={() => setOpen(false)}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(var(--shadow-rgb),0.72)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          opacity: open ? 1 : 0,
          transition: "opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)",
          pointerEvents: open ? "auto" : "none",
          zIndex: 1400,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "max(12px, env(safe-area-inset-top))",
          left: 0,
          right: 0,
          display: "flex",
          // Mobile abierto: menú anclado a la derecha (dropdown). Otros
          // casos (desktop, /turno mobile cerrado, etc.): chip centrado.
          justifyContent: {
            xs: open || registroEnBarra ? "flex-end" : "center",
            sm: "center",
          },
          pr: { xs: open || registroEnBarra ? "8px" : 0, sm: 0 },
          // z-index condicional: cuando el menú está abierto, el navbar
          // tiene que estar SOBRE fab-chat/tutorial-pill (1299/1300) y el
          // Dialog del viewer (1300). Cuando está cerrado, queda DEBAJO
          // del Dialog para que el visualizador full-screen lo tape.
          zIndex: open ? 1500 : 1100,
          pointerEvents: "none",
        }}
      >
        <Paper
          data-testid="navbar-pill"
          elevation={open ? 10 : 4}
          // Cerrar al salir el mouse del menú expandido (solo desktop con hover real —
          // en mobile/touch el mouseleave dispara espuriamente). Espeja la lógica de
          // apertura por hover en el trigger de abajo.
          onMouseLeave={() => {
            if (open && window.matchMedia("(hover: hover)").matches) setOpen(false);
          }}
          sx={{
            pointerEvents: "auto",
            borderRadius: open ? 3 : 999,
            px: open ? { xs: 1.5, sm: 3 } : { xs: 2, sm: 3.5 },
            py: open ? { xs: 1.25, sm: 2.5 } : { xs: 0.8, sm: 1.2 },
            display: "flex",
            // Abierto: SIEMPRE columna (logo arriba, menú full-width abajo).
            // Cerrado: fila (logo + hamburguesa en línea dentro del pill).
            flexDirection: open ? "column" : "row",
            alignItems: open ? "stretch" : "center",
            gap: open ? 2 : { xs: 1.5, sm: 5 },
            backdropFilter: "blur(16px)",
            // backgroundImage:"none" mata el overlay gris de elevación de MUI
            // en dark mode; al abrir agregamos un tinte teal sutil arriba.
            backgroundColor: themeMode === "dark" ? "rgba(11,13,18,0.94)" : "rgba(248,250,252,0.96)",
            backgroundImage: open
              ? "linear-gradient(180deg, rgba(31,111,151,0.08), rgba(31,111,151,0) 200px)"
              : "none",
            border: "1px solid rgba(var(--fg-rgb),0.14)",
            opacity: open ? 1 : visible ? 1 : 0,
            transform: open
              ? "translateY(6px) scale(1)"
              : visible
                ? "translateY(0)"
                : "translateY(-12px)",
            // Transición unificada — todas las propiedades visuales animan con
            // la misma curva y duración para que el cambio chip→menú se vea
            // fluido. Las props GPU (transform, opacity, box-shadow) animan
            // rápido; las de layout (width, padding, gap, border-radius,
            // max-height) animan suave con el mismo easing.
            transition: [
              "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              "opacity 220ms cubic-bezier(0.22, 1, 0.36, 1)",
              "box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              "border-radius 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              "width 320ms cubic-bezier(0.22, 1, 0.36, 1)",
              "padding 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              "gap 280ms cubic-bezier(0.22, 1, 0.36, 1)",
              "max-height 320ms cubic-bezier(0.22, 1, 0.36, 1)",
              "background-color 200ms ease",
            ].join(", "),
            width: open
              ? { xs: 340, sm: 600, md: 720 }
              : registroEnBarra
                ? "auto"
                : { xs: 220, sm: 280 },
            maxWidth: "calc(100% - 16px)",
            maxHeight: open ? { xs: "80dvh", sm: "calc(100vh - 80px)" } : { xs: 60, sm: 60 },
            boxShadow: open
              ? "0 18px 50px rgba(var(--shadow-rgb),0.55)"
              : "0 8px 20px rgba(var(--shadow-rgb),0.28)",
            position: "relative",
            overflow: open ? "hidden auto" : "hidden",
            // Hint GPU para que el browser prepare composite layer y la animación
            // sea fluida en mobile (sin esto, primer abrir tarda más).
            willChange: "width, max-height, transform, opacity",
            // Compactar listado del menú en mobile: items más chicos y
            // densos (Opción A "compacto vertical").
            "& .MuiListItemButton-root": {
              py: { xs: 0.5, sm: 1 },
              minHeight: { xs: 36, sm: 44 },
            },
            "& .MuiListItemText-primary, & .MuiListItemText-primary > .MuiTypography-root":
              {
                fontSize: { xs: 14, sm: 16 },
                fontWeight: 600,
                lineHeight: 1.25,
              },
          }}
        >
          {open && (
            <IconButton
              onClick={handleToggleMenu}
              sx={{
                position: "absolute",
                top: "max(12px, env(safe-area-inset-top))",
                right: "max(12px, env(safe-area-inset-right))",
                backgroundColor: themeMode === "dark" ? "rgba(var(--shadow-rgb),0.45)" : "rgba(var(--fg-rgb),0.07)",
                color: "text.primary",
                "&:hover": { backgroundColor: themeMode === "dark" ? "rgba(var(--shadow-rgb),0.55)" : "rgba(var(--fg-rgb),0.12)" },
                zIndex: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              gap: registroEnBarra ? 1 : 2,
              cursor: open ? "default" : "pointer",
            }}
            // Tocar el chip entero (logo + ≡) abre el menú con un solo tap.
            // En desktop, el hover queda restringido a (hover: hover) para
            // que en mobile no dispare el "phantom mouseenter" que causaba
            // el doble-tap (el primer tap abría y el click lo cerraba).
            onClick={() => { if (!open) setOpen(true); }}
            onMouseEnter={(e) => {
              if (window.matchMedia("(hover: hover)").matches) setOpen(true);
            }}
          >
            <SmardxTitle size={registroEnBarra ? 26 : 32} />
            {registroEnBarra && (
              <Button
                data-testid="navbar-registrarse"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNav("/authentication?register=true");
                }}
                sx={{ ...crearCuentaSx, minHeight: 44, height: 44, px: 1.75, fontSize: 14 }}
              >
                Crear cuenta
              </Button>
            )}
            <IconButton
              // setOpen(true) idempotente — no hace toggle: solo abre. Cerrar
              // queda en la X absolute (handleToggleMenu) y en el overlay.
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              sx={{
                display: open ? "none" : "flex",
                backgroundColor: "transparent",
                color: "text.primary",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "rgba(var(--fg-rgb),0.08)",
                },
                position: "relative",
                width: 40,
                height: 40,
              }}
            >
              <Box sx={{ position: "relative", width: 24, height: 24 }}>
                <MenuIcon
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: open ? 0 : 1,
                    transform: open ? "scale(0.6) rotate(45deg)" : "scale(1)",
                    transition: "all 220ms ease",
                  }}
                />
                <CloseIcon
                  sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: open ? 1 : 0,
                    transform: open ? "scale(1)" : "scale(0.6) rotate(-45deg)",
                    transition: "all 220ms ease",
                  }}
                />
              </Box>
            </IconButton>
          </Stack>

          <Collapse in={open} timeout={{ enter: 320, exit: 220 }} unmountOnExit easing={{ enter: "cubic-bezier(0.22, 1, 0.36, 1)", exit: "cubic-bezier(0.22, 1, 0.36, 1)" }}>
          <Box
            sx={{
              // En mobile dejamos más espacio arriba para que la X de cerrar
              // (top:12 right:12, ~40px) no se choque con el primer item.
              mt: { xs: 5, sm: 2 },
              // minWidth = ancho final del contenido. Evita que las chips/cards
              // refloween mientras el Paper morfea su ancho (280→720): así el
              // Collapse mide la ALTURA final desde el arranque y no hay
              // overshoot ("crece de más y luego se achica"). El overflow-x
              // temporal queda clippeado por el Paper (overflow: hidden auto).
              minWidth: { xs: 300, sm: 540, md: 672 },
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-8px)",
              transition: "opacity 240ms cubic-bezier(0.22, 1, 0.36, 1) 80ms, transform 280ms cubic-bezier(0.22, 1, 0.36, 1) 80ms",
            }}
          >
            {/* ===== Explorar (secciones del landing) ===== */}
            <Typography sx={secLabelSx}>Explorar</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
              {sectionItems.map((item) => (
                <Box key={item.path} onClick={() => handleNav(item.path)} sx={item.path === "/" ? chipHomeSx : chipBaseSx}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 17 } })}
                  {item.label}
                </Box>
              ))}
            </Box>

            {/* ===== Herramientas (modos, según permisos) ===== */}
            {functionalItems.length > 0 && (
              <>
                <Typography sx={secLabelSx}>Herramientas</Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  {functionalItems.map((item) => (
                    <Box key={item.path} onClick={() => handleNav(item.path)} sx={cardSx}>
                      <Box sx={cardIcoSx}>{React.cloneElement(item.icon, { sx: { fontSize: 19 } })}</Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.2 }} noWrap>
                          {item.short}
                        </Typography>
                        {item.sub && (
                          <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.15 }} noWrap>
                            {item.sub}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {/* ===== Footer: cuenta / auth / acciones mobile ===== */}
            {/* Con sesión el pie trae la cuenta; sin sesión, los dos botones de
                acceso, también en escritorio (#1439). */}
            {(
              <Box
                sx={{
                  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1,
                  pt: 1.75, borderTop: "1px solid rgba(var(--fg-rgb),0.08)",
                }}
              >
                {/* Toggle theme — siempre visible en mobile, también sin login. Solo ícono. */}
                {isMobile && (
                  <Box
                    onClick={() => { setOpen(false); toggleTheme(); }}
                    role="button"
                    aria-label={themeMode === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
                    sx={{ ...chipBaseSx, px: 1.25, gap: 0 }}
                  >
                    {themeMode === "dark"
                      ? <Brightness7Icon sx={{ fontSize: 18 }} />
                      : <Brightness4Icon sx={{ fontSize: 18 }} />}
                  </Box>
                )}
                {/* "Instalar app": reabre la guía del modo celular. Solo en
                    celular y solo si todavía NO está instalada — una vez que la
                    app corre en standalone el chip no tiene nada que ofrecer.
                    Va fuera del bloque de login porque el momento natural para
                    instalarla es ANTES de tener cuenta. */}
                {isMobile && !isStandalone() && (
                  <Box
                    onClick={() => { setOpen(false); openInstallGuide(); }}
                    role="button"
                    aria-label="Instalar SmartDx en el celular"
                    sx={chipBaseSx}
                  >
                    <AddToHomeScreenIcon sx={{ fontSize: 16 }} />Instalar app
                  </Box>
                )}
                {!currentUser ? (
                  // Mobile: los dos a todo el ancho, "Crear cuenta gratis" arriba.
                  // Escritorio: a la derecha, en la misma fila.
                  <Box
                    sx={{
                      display: "flex", gap: 1, ml: { sm: "auto" },
                      flexDirection: { xs: "column-reverse", sm: "row" },
                      flexBasis: { xs: "100%", sm: "auto" }, mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <Button
                      data-testid="navbar-menu-ingresar"
                      onClick={() => handleNav("/authentication")}
                      sx={{ ...ingresarSx, height: 44, border: "1px solid rgba(var(--fg-rgb),0.16)", px: 2.25 }}
                    >
                      Ingresar
                    </Button>
                    <Button
                      data-testid="navbar-menu-crear-cuenta"
                      onClick={() => handleNav("/authentication?register=true")}
                      sx={{ ...crearCuentaSx, height: 44, px: 2.5 }}
                    >
                      Crear cuenta gratis
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* El uso semanal SIEMPRE está en el menú, en su versión
                        expandida: es la única superficie donde vive bajo 1200
                        px, y arriba de eso evita que el dato desaparezca de la
                        pantalla justo cuando el menú tapa la píldora (la
                        píldora vive en la rama `!open`). */}
                    <WeeklyUsagePill variant="expanded" />
                    {accountItems.map((item) => (
                      <Box key={item.path} onClick={() => handleNav(item.path)} sx={chipBaseSx}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 16 } })}
                        {item.label}
                      </Box>
                    ))}
                    {/* En mobile el cluster superior está oculto → traemos sus
                        acciones (recargar / perfil / salir) acá. El toggle tema
                        ya se renderea arriba, fuera del bloque currentUser, así
                        que está disponible también sin login. */}
                    {isMobile && (
                      <>
                        <Box sx={{ flexBasis: "100%", height: 0 }} />
                        <Box onClick={() => { setOpen(false); reloadPage(); }} sx={chipBaseSx}>
                          <Cached sx={{ fontSize: 16 }} />Recargar
                        </Box>
                        <Box onClick={() => { setOpen(false); abrirAjustes("perfil"); }} sx={chipBaseSx}>
                          <Avatar
                            src={currentUser.professional_profile?.profile_photo || undefined}
                            sx={{ width: 20, height: 20, fontSize: 9, fontWeight: 700, background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-soft))" }}
                          >
                            {initials}
                          </Avatar>
                          Perfil
                        </Box>
                        {/* Espejo obligatorio de Ajustes: el cluster de
                            escritorio (engranaje) NO existe en mobile, así que
                            sin esto desde el teléfono no se llega a cambiar la
                            clave ni el tema. Abre la lista de submenús. */}
                        <Box
                          onClick={() => { setOpen(false); abrirAjustes(null); }}
                          sx={chipBaseSx}
                        >
                          <SettingsIcon sx={{ fontSize: 16 }} />Ajustes
                        </Box>
                        <Box onClick={logout} sx={{ ...chipBaseSx, color: "var(--color-danger)", "& svg": { color: "var(--color-danger)" } }}>
                          <Logout sx={{ fontSize: 16 }} />Salir
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
          </Collapse>
        </Paper>
        {!isMobile && !open && (
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            position: "absolute",
            right: { xs: 12, sm: 20 },
            top: "env(safe-area-inset-top)",
            height: 60,
            alignItems: "center",
            // Por debajo del Dialog del viewer (1300) para que el viewer
            // full-screen tape el cluster.
            zIndex: 1100,
            pointerEvents: "auto",
          }}
        >
          {/* Uso semanal (#1209): píldora SUELTA, fuera del cluster y a su
              izquierda (mockup E · posición 3). No es un control: es un dato de
              la cuenta, así que no entra al contenedor de botones. Se dibuja
              sola solo si hay sesión y si el endpoint respondió. */}
          {!!currentUser && hayEspacioAncho && (
            <WeeklyUsagePill variant={cabePildoraCompleta ? "pill" : "mini"} />
          )}
          {/* Cluster glass segmentado — mismo lenguaje que el pill central:
              los controles viven en UN contenedor en vez de íconos sueltos.
              (Variante A del mockup tools/mockups/smartdx_mockups_header.html) */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{
              p: "5px",
              borderRadius: 999,
              backgroundColor: themeMode === "dark" ? "rgba(var(--shadow-rgb),0.6)" : "rgba(var(--fg-rgb),0.07)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid rgba(var(--fg-rgb),0.1)",
              boxShadow: "0 8px 20px rgba(var(--shadow-rgb),0.24)",
              // Botones de ícono dentro del cluster: borderless, 36px, hover sutil.
              "& .cluster-ic": {
                width: 36,
                height: 36,
                color: "text.primary",
                border: 0,
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.1)" },
              },
            }}
          >
            {!!currentUser && (
              <Tooltip title="Ajustes">
                <IconButton className="cluster-ic" onClick={() => abrirAjustes("general")}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Recargar">
              <IconButton className="cluster-ic" onClick={reloadPage}>
                <Cached fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={themeMode === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}>
              <IconButton className="cluster-ic" onClick={toggleTheme} aria-label="toggle theme">
                {themeMode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Box sx={{ width: "1px", height: 22, backgroundColor: "rgba(var(--fg-rgb),0.1)", mx: 0.5 }} />

            {!currentUser ? (
              <>
                {/* #1453 R13: el registro a la vista en el header de escritorio.
                    #1439: "Acceder" + menú (Iniciar sesión / Crear cuenta) pasa a
                    "Ingresar" directo; los dos caminos quedan a un clic. */}
                <Button
                  data-testid="navbar-ingresar"
                  onClick={() => handleNav("/authentication")}
                  sx={{ ...ingresarSx, ml: "2px" }}
                >
                  Ingresar
                </Button>
                <Button
                  data-testid="navbar-crear-cuenta"
                  onClick={() => handleNav("/authentication?register=true")}
                  sx={{ ...crearCuentaSx, mr: "2px" }}
                >
                  Crear cuenta gratis
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="Mi perfil">
                  <Box
                    data-testid="navbar-user"
                    onClick={() => abrirAjustes("perfil")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.85,
                      pl: "2px",
                      pr: displayName && hayEspacioAncho ? 1.25 : "2px",
                      py: "2px",
                      borderRadius: 999,
                      cursor: "pointer",
                      maxWidth: 220,
                      transition: "background 0.14s",
                      "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.1)" },
                    }}
                  >
                    <Avatar
                      src={currentUser.professional_profile?.profile_photo || undefined}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: 12.5,
                        fontWeight: 700,
                        flex: "0 0 auto",
                        border: "1.5px solid rgba(var(--fg-rgb),0.18)",
                        background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-soft))",
                      }}
                    >
                      {initials || "?"}
                    </Avatar>
                    {displayName && hayEspacioAncho && (
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          // Con la píldora completa al lado, 160 px de nombre
                          // dejaban la píldora a 1 px del pill del logo (y
                          // solapando 6 px con un porcentaje de 4 dígitos).
                          // 130 devuelve ~30 px de aire sin sacar el nombre.
                          maxWidth: cabePildoraCompleta ? 130 : 160,
                        }}
                      >
                        {displayName}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
                <Tooltip title="Cerrar sesión">
                  <IconButton className="cluster-ic" onClick={logout}>
                    <Logout fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>
        )}
      </Box>
      {dialogoAjustes}
      {/* Spacer para que el contenido no quede tapado por el navbar fixed.
          En /turno mobile lo reducimos a 0 — el cockpit V2 controla su propia
          altura via 100dvh y no necesita el offset. */}
      <Box sx={{ height: isMobileTurno ? 0 : 72 }} />
    </>
  );
};

export default DxNavbar;
